import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { createAccount } from '../services/api';
import { useDialogClickOutside, useModalScrollLock } from '../hooks';
import { Input, CustomSelect, LoadingButton } from '@redpanda/ui';
import { FormFieldWithError, getAccountTypeOptions } from './forms';
import { toast } from 'react-hot-toast';

interface AccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAccountCreated?: (account: any) => void;
}

interface FormData {
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  description?: string;
}

const AccountDialog: React.FC<AccountDialogProps> = ({
  isOpen,
  onClose,
  onAccountCreated
}) => {
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue, setError } = useForm<FormData>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  
  const { t } = useTranslation();
  
  // 鎖定背景滾動
  useModalScrollLock(isOpen);

  const handleClose = () => {
    setSubmitError(null);
    reset();
    onClose();
  };

  // 點擊外部關閉 dialog
  const handleBackgroundClick = useDialogClickOutside(handleClose);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setSubmitError(null);

    try {
      const response = await createAccount(data);
      toast.success(t('common.messages.createSuccess'));
      handleClose();
      if (onAccountCreated) {
        onAccountCreated(response.data);
      }
    } catch (error: any) {
      console.error('Failed to create account:', error);

      // 處理重複錯誤：顯示在欄位下方，如同必填驗證
      if (error.response?.status === 409) {
        setError('code', {
          type: 'manual',
          message: t('accounts.toast.duplicateCode')
        });
      } else if (error.response?.data?.error) {
        setSubmitError(error.response.data.error);
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        setSubmitError(error.response.data.errors.map((err: any) => err.msg).join(', '));
      } else {
        setSubmitError(t('accounts.toast.createFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div 
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto overflow-x-visible"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 對話框標題 */}
        <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('accounts.add')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 對話框內容 */}
        <div className="p-6">
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 overflow-visible">

                <FormFieldWithError
                  label={t('accounts.code')}
                  required={true}
                  error={errors.code?.message}
                  htmlFor="code"
                >
                  <Input
                    id="code"
                    type="text"
                    {...register('code', {
                      required: t('common.forms.validation.required'),
                      pattern: {
                        value: /^[0-9]{4,6}$/,
                        message: t('common.form.accountCodePattern')
                      }
                    })}
                    variant={errors.code ? "error" : "default"}
                  />
                </FormFieldWithError>

                <FormFieldWithError
                  label={t('accounts.name')}
                  required={true}
                  error={errors.name?.message}
                  htmlFor="name"
                >
                  <Input
                    id="name"
                    type="text"
                    {...register('name', { required: t('common.forms.validation.required') })}
                    variant={errors.name ? "error" : "default"}
                  />
                </FormFieldWithError>

                <FormFieldWithError
                  label={t('accounts.type')}
                  required={true}
                  error={errors.type?.message}
                  htmlFor="type"
                >
                  <CustomSelect
                    id="type"
                    {...register('type', { required: t('common.forms.validation.required') })}
                    value={watch('type') || ''}
                    onChange={(value) => setValue('type', value as 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE')}
                    variant={errors.type ? "error" : "default"}
                    options={[
                      { value: '', label: t('accounts.dialog.selectType') },
                      ...getAccountTypeOptions(t)
                    ]}
                  />
                </FormFieldWithError>

                <FormFieldWithError
                  label={t('accounts.description')}
                  required={false}
                  error={errors.description?.message}
                  htmlFor="description"
                >
                  <Input
                    id="description"
                    type="text"
                    {...register('description')}
                    variant={errors.description ? "error" : "default"}
                  />
                </FormFieldWithError>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse pt-6 border-t border-gray-200 dark:border-slate-700">
                  <LoadingButton
                    loading={loading}
                    type="submit"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                  >
                    {t('common.actions.add')}
                  </LoadingButton>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md"
                    onClick={handleClose}
                  >
                    {t('common.actions.cancel')}
                  </button>
                </div>
                
                {/* 錯誤訊息區塊 - 比照 CRM QuoteDialog */}
                {submitError && (
                  <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
                    {submitError}
                  </div>
                )}
              </form>
        </div>
      </div>
    </div>
  );
};

export default AccountDialog;